/* eslint-disable max-len */
import { isObject } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useCreatePackageSettingMutation } from '../../../../api/mutations/createPackageSetting';
import { useDeletePackageSettingMutation } from '../../../../api/mutations/deletePackageSetting';
import { useDeleteVariantConfiguratorPackageSectionImageAssetMutation } from '../../../../api/mutations/deleteVariantConfiguratorPackageSectionImageAsset';
import { useUpdatePackageSettingMutation } from '../../../../api/mutations/updatePackageSetting';
import { useUploadVariantConfiguratorPackageSectionImageAssetMutation } from '../../../../api/mutations/uploadVariantConfiguratorPackageSectionImageAsset';
import { VariantConfiguratorAsset } from '../../../../api/types';
import useHandleAdditionalDetails from './useHandleAdditionalDetails';
import { type PackageSettingValues } from './utils';

const useHandlePackageSetting = () => {
    const [createPackageSettingMutation] = useCreatePackageSettingMutation();
    const [updatePackageSettingMutation] = useUpdatePackageSettingMutation();
    const [deletePackageSettingMutation] = useDeletePackageSettingMutation();

    const [uploadPackageSectionImageMutation] = useUploadVariantConfiguratorPackageSectionImageAssetMutation();
    const [deletePackageSectionImageMutation] = useDeleteVariantConfiguratorPackageSectionImageAssetMutation();

    const { createAdditionalDetails, updateAdditionalDetails, deleteAdditionalDetails } = useHandleAdditionalDetails();

    // create package setting execution
    const createPackageSetting = useCallback(
        async (variantConfiguratorId: string, blockId: string, settings: PackageSettingValues) => {
            const result = await createPackageSettingMutation({
                variables: {
                    blockId,
                    settings: {
                        features: settings.features,
                        packageName: settings.packageName,
                        packageType: settings.packageType,
                    },
                    variantConfiguratorId,
                },
            });

            const packageSettings = result.data?.packageSettings;

            if (packageSettings) {
                // Create package setting asset
                if (settings.sectionImage instanceof File) {
                    await uploadPackageSectionImageMutation({
                        variables: {
                            blockId,
                            id: variantConfiguratorId,
                            packageSettingId: packageSettings.id,
                            type: VariantConfiguratorAsset.PackageSectionImage,
                            upload: settings.sectionImage,
                        },
                    });
                }

                // Create additional details
                await Promise.all(
                    (packageSettings.additionalDetails ?? []).map(additionalDetail =>
                        createAdditionalDetails(packageSettings.id, variantConfiguratorId, blockId, additionalDetail)
                    )
                );
            }
        },
        [createAdditionalDetails, createPackageSettingMutation, uploadPackageSectionImageMutation]
    );

    // Update package setting execution
    const updatePackageSetting = useCallback(
        async (packageSetting: PackageSettingValues, blockId: string, variantConfiguratorId: string) => {
            const { data } = await updatePackageSettingMutation({
                variables: {
                    blockId,
                    packageSettingsId: packageSetting.id,
                    settings: {
                        features: packageSetting.features,
                        packageName: packageSetting.packageName,
                        packageType: packageSetting.packageType,
                    },
                    variantConfiguratorId,
                },
            });

            const initialPackageSetting = data?.variantConfigurator?.packageBlock?.packageSettings?.find(
                setting => setting.id === packageSetting.id
            );

            // Update package setting asset
            if (packageSetting.sectionImage instanceof File) {
                await uploadPackageSectionImageMutation({
                    variables: {
                        blockId,
                        id: variantConfiguratorId,
                        packageSettingId: packageSetting.id,
                        type: VariantConfiguratorAsset.PackageSectionImage,
                        upload: packageSetting.sectionImage,
                    },
                });
            } else if (!packageSetting.sectionImage && isObject(initialPackageSetting.sectionImage)) {
                await deletePackageSectionImageMutation({
                    variables: {
                        blockId,
                        id: variantConfiguratorId,
                        packageSettingId: packageSetting.id,
                        type: VariantConfiguratorAsset.PackageSectionImage,
                    },
                });
            }

            const additionalDetails = packageSetting.additionalDetails ?? [];

            // Delete additional detail first
            const additionalDetailToDelete = initialPackageSetting?.additionalDetails?.map(item => {
                const foundAdditionalDetail = additionalDetails.find(
                    additionalDetail => additionalDetail.id === item.id
                );

                if (!foundAdditionalDetail) {
                    return deleteAdditionalDetails(blockId, variantConfiguratorId, initialPackageSetting.id, item.id);
                }

                return Promise.resolve();
            });

            if (additionalDetailToDelete.length) {
                await Promise.all(additionalDetailToDelete);
            }

            // Create or update additional detail
            const additionalDetailPromises = (packageSetting.additionalDetails ?? []).map(additionalDetail => {
                if (!additionalDetail?.id) {
                    return createAdditionalDetails(packageSetting.id, variantConfiguratorId, blockId, additionalDetail);
                }

                const initialAdditionalDetail = initialPackageSetting?.additionalDetails?.find(
                    item => item.id === additionalDetail?.id
                );

                return updateAdditionalDetails(
                    variantConfiguratorId,
                    blockId,
                    packageSetting,
                    additionalDetail,
                    initialAdditionalDetail
                );
            });

            if (additionalDetailPromises.length) {
                // Update additional details
                await Promise.all(additionalDetailPromises);
            }
        },
        [
            createAdditionalDetails,
            deleteAdditionalDetails,
            deletePackageSectionImageMutation,
            updateAdditionalDetails,
            updatePackageSettingMutation,
            uploadPackageSectionImageMutation,
        ]
    );

    // Delete package setting execution
    const deletePackageSetting = useCallback(
        async (
            packageSetting: Pick<PackageSettingValues, 'id' | 'additionalDetails'>,
            blockId: string,
            variantConfiguratorId: string
        ) => {
            // Delete package setting assets
            await deletePackageSectionImageMutation({
                variables: {
                    blockId,
                    id: variantConfiguratorId,
                    packageSettingId: packageSetting.id,
                    type: VariantConfiguratorAsset.PackageSectionImage,
                },
            });

            // Delete additional details assets
            await Promise.all(
                (packageSetting.additionalDetails ?? []).map(additionalDetail =>
                    additionalDetail?.id
                        ? deleteAdditionalDetails(
                              blockId,
                              variantConfiguratorId,
                              packageSetting.id,
                              additionalDetail.id
                          )
                        : Promise.resolve(null)
                )
            );

            // finally delete package setting mutation
            await deletePackageSettingMutation({
                variables: {
                    blockId,
                    packageSettingsId: packageSetting.id,
                    variantConfiguratorId,
                },
            });
        },
        [deleteAdditionalDetails, deletePackageSectionImageMutation, deletePackageSettingMutation]
    );

    return useMemo(
        () => ({ createPackageSetting, updatePackageSetting, deletePackageSetting }),
        [createPackageSetting, deletePackageSetting, updatePackageSetting]
    );
};

export default useHandlePackageSetting;
